export type ThemeVariant = 'default' | 'minimal' | 'vibrant' | 'professional'

export interface ThemeConfig {
  name: string
  description: string
  styles: {
    // Layout configs
    borderRadius: {
      sm: string
      md: string
      lg: string
      xl: string
    }
    spacing: {
      section: string
      container: string
      card: string
    }
    typography: {
      fontFamily: string
      headingWeight: string
      bodyWeight: string
    }
    effects: {
      shadow: string
      blur: string
      gradient: string
    }
    animation: {
      duration: string
      easing: string
    }
  }
  components: {
    card: {
      variant: 'default' | 'bordered' | 'shadow' | 'minimal'
      padding: string
    }
    button: {
      variant: 'default' | 'rounded' | 'sharp' | 'pill'
    }
    navbar: {
      style: 'default' | 'minimal' | 'floating' | 'sidebar'
      height: string
    }
  }
}

export const themes: Record<ThemeVariant, ThemeConfig> = {
  default: {
    name: 'RifaBR Padrão',
    description: 'Design moderno e elegante',
    styles: {
      borderRadius: {
        sm: '0.375rem',
        md: '0.5rem', 
        lg: '0.75rem',
        xl: '1rem'
      },
      spacing: {
        section: '5rem',
        container: '2rem',
        card: '1.5rem'
      },
      typography: {
        fontFamily: 'Inter, system-ui, sans-serif',
        headingWeight: '700',
        bodyWeight: '400'
      },
      effects: {
        shadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
        blur: '16px',
        gradient: 'linear-gradient(135deg, var(--primary) 0%, #8b5cf6 100%)'
      },
      animation: {
        duration: '0.3s',
        easing: 'cubic-bezier(0.4, 0, 0.2, 1)'
      }
    },
    components: {
      card: {
        variant: 'shadow',
        padding: '1.5rem'
      },
      button: {
        variant: 'default'
      },
      navbar: {
        style: 'default',
        height: '4rem'
      }
    }
  },
  minimal: {
    name: 'Minimalista',
    description: 'Design clean e direto',
    styles: {
      borderRadius: {
        sm: '0.25rem',
        md: '0.375rem',
        lg: '0.5rem', 
        xl: '0.75rem'
      },
      spacing: {
        section: '3rem',
        container: '1.5rem',
        card: '1rem'
      },
      typography: {
        fontFamily: 'system-ui, sans-serif',
        headingWeight: '600',
        bodyWeight: '400'
      },
      effects: {
        shadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        blur: '8px',
        gradient: 'linear-gradient(90deg, var(--primary) 0%, var(--primary) 100%)'
      },
      animation: {
        duration: '0.2s',
        easing: 'ease-out'
      }
    },
    components: {
      card: {
        variant: 'bordered',
        padding: '1rem'
      },
      button: {
        variant: 'sharp'
      },
      navbar: {
        style: 'minimal',
        height: '3.5rem'
      }
    }
  },
  vibrant: {
    name: 'Vibrante',
    description: 'Cores vivas e dinâmicas',
    styles: {
      borderRadius: {
        sm: '0.5rem',
        md: '0.75rem',
        lg: '1rem',
        xl: '1.5rem'
      },
      spacing: {
        section: '6rem',
        container: '2.5rem',
        card: '2rem'
      },
      typography: {
        fontFamily: 'Inter, sans-serif',
        headingWeight: '800',
        bodyWeight: '500'
      },
      effects: {
        shadow: '0 20px 40px -10px rgba(0, 0, 0, 0.15)',
        blur: '20px',
        gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)'
      },
      animation: {
        duration: '0.4s',
        easing: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)'
      }
    },
    components: {
      card: {
        variant: 'shadow',
        padding: '2rem'
      },
      button: {
        variant: 'rounded'
      },
      navbar: {
        style: 'floating',
        height: '4.5rem'
      }
    }
  },
  professional: {
    name: 'Profissional',
    description: 'Elegante e corporativo',
    styles: {
      borderRadius: {
        sm: '0.125rem',
        md: '0.25rem',
        lg: '0.375rem',
        xl: '0.5rem'
      },
      spacing: {
        section: '4rem',
        container: '2rem',
        card: '1.25rem'
      },
      typography: {
        fontFamily: 'ui-serif, Georgia, serif',
        headingWeight: '700',
        bodyWeight: '400'
      },
      effects: {
        shadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        blur: '12px',
        gradient: 'linear-gradient(180deg, var(--primary) 0%, #1e293b 100%)'
      },
      animation: {
        duration: '0.25s',
        easing: 'ease-in-out'
      }
    },
    components: {
      card: {
        variant: 'minimal',
        padding: '1.25rem'
      },
      button: {
        variant: 'sharp'
      },
      navbar: {
        style: 'default',
        height: '4rem'
      }
    }
  }
}

// Por enquanto, sempre retorna o tema default
// No futuro, pode ser baseado em configuração do usuário, hora do dia, etc.
export function getCurrentTheme(): ThemeConfig {
  // Aqui poderia haver lógica para:
  // - Detectar preferência do usuário
  // - Alternar por hora do dia
  // - A/B testing
  // - Configuração admin
  
  return themes.default
}

// Hook para facilitar o uso nos componentes
export function useCurrentTheme() {
  return getCurrentTheme()
}